import { useSelector } from 'react-redux';

import { hasFix, NO_SAFE_VERSION } from '../../../../../shared/vulns/remediation';
import * as IssuesV2Store from '../../../../redux/issuesV2';
import type { VulnerabilityIssue } from '../../../../../shared/issuesV2';

/**
 * Which unfixable state a vulnerability is in. Mirrors the two remediation
 * sentinels: 'no-fix' is NO_SAFE_VERSION (we calculated and no version fixes
 * it), 'unable-to-calculate' is UNABLE_TO_CALCULATE (we could not work it out).
 */
export type FossabotNoFixType = 'no-fix' | 'unable-to-calculate';

/**
 * Reads the active vulnerability issue from Redux and reports which unfixable
 * state the Remediation panel should show, or null when either fix is a real
 * version and the panel should offer it instead.
 *
 * The two remediation fields are calculated independently on the backend and
 * each falls back to UNABLE_TO_CALCULATE, so they can disagree. The choice is
 * driven by partialFix because that is the field about this vulnerability;
 * completeFix is about every vulnerability on the dependency.
 */
export const useFossabotNoFix = (): FossabotNoFixType | null => {
  const issue = useSelector(IssuesV2Store.selectors.getIssueDetails) as VulnerabilityIssue | null;

  if (!issue) {
    return null;
  }

  const { partialFix, completeFix } = issue.remediation;

  if (hasFix(partialFix) || hasFix(completeFix)) {
    return null;
  }

  return partialFix === NO_SAFE_VERSION ? 'no-fix' : 'unable-to-calculate';
};
